import { useEffect, useRef, useState } from "react";
import { shortDate } from "../lib/weekLine";
import type { CalendarItem } from "../lib/calendar";
import { supabase } from "../lib/supabase";
import { removeAssignment, type AssignmentWriter } from "../lib/assignmentActions";
import { taskRemovalConsequence } from "../lib/assignmentEdit";

/* 🔴 JOB 3 STEP 5, PAGE 1 (2026-10-09) — ONE CALENDAR: THE TASKS CALENDAR FOLDED INTO THE TRAINING ONE.
 *
 * The Week tab drew the same grid twice (Training, then Tasks: `Today ‹ range › 1·2·4 weeks` and seven `+` per week, each),
 * and the Training cell already said `+ N tasks`. That line now opens THIS window for its day: the day's tasks, each with
 * `Remove…` (the consequence asked in place, `taskRemovalConsequence`, the sentence the Tasks calendar's `⋯` asked), and
 * `Add a task` for that day (the page's `TaskEditor`). A day with no tasks carries a quiet `+ Task` on the cell itself, so
 * the first task on a day is one click and never passes through an empty window.
 *
 * ⚠️ WHAT THE TASKS CALENDAR SAID, THIS SAYS: the tick means "they trained that day" (a task has no set log of its own),
 * and an OPTIONAL task carries the word, never the tick (his 2026-10-07) — both computed once, in the calendar's items.
 * ⚠️ A NATIVE MODAL `<dialog>` (the house window), Escape closes it, focus lands on its title and returns to the line that
 * opened it (the calendar holds the opener).
 */
export function DayTasksWindow({ dayKey, tasks, onAddTask, onChanged, onClose }: {
  dayKey: string;
  /** The day's TASK items, from the calendar's own read (hidden rows already excluded there). */
  tasks: readonly CalendarItem[];
  /** Absent = no add control (a non-active link: a task nobody can receive). */
  onAddTask?: (dayKey: string) => void;
  /** Bumped after a removal, so the page re-reads the client. */
  onChanged: () => void;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const [removing, setRemoving] = useState<{ id: string; name: string; done: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (el !== null && !el.open) el.showModal();
    heading.current?.focus();
    return () => { if (el !== null && el.open) el.close(); };
  }, []);

  async function confirmRemove(): Promise<void> {
    if (removing === null) return;
    setBusy(true);
    const r = await removeAssignment(supabase as unknown as AssignmentWriter, removing.id);
    setBusy(false);
    setSaid(r.said);
    if (!r.ok) return;
    const last = tasks.length <= 1;
    setRemoving(null);
    onChanged();
    // The day's last task gone: nothing is left to manage here, so the window closes rather than drawing an empty list.
    if (last) onClose();
  }

  const c = removing === null ? null : taskRemovalConsequence(removing.name, dayKey, removing.done);
  return (
    <dialog ref={dialogRef} className="ht-window ht-confirm day-tasks-window" aria-labelledby="day-tasks-h"
            onCancel={(e) => { e.preventDefault(); onClose(); }}>
      <div className="ht-window-frame ht-confirm-frame">
        <h2 className="ht-title ht-confirm-title" id="day-tasks-h" tabIndex={-1} ref={heading}>
          {c === null ? `Tasks · ${shortDate(dayKey)}` : c.headline}
        </h2>
        {c === null ? (
          <ul className="day-tasks-list">
            {tasks.map((t) => (
              <li key={t.id} className="ht-body day-tasks-row">
                {t.trained && <span aria-hidden="true" className="calendar-item-mark">✓</span>}
                <span className="day-tasks-name">{t.name}</span>
                {t.optional === true && <span className="calendar-item-optional">optional</span>}
                <button type="button" className="ours-text-action day-tasks-remove"
                        aria-label={`Remove ${t.name} on ${shortDate(dayKey)}…`}
                        onClick={() => { setSaid(null); setRemoving({ id: t.id, name: t.name, done: t.trained }); }}>
                  Remove…
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="ht-body">{c.detail}</p>
        )}
        {said !== null && <p className="ht-caption" role="status">{said}</p>}
        <div className="ht-confirm-actions">
          {c === null ? (
            <>
              <button type="button" className="ht-button" onClick={onClose}>Close</button>
              {onAddTask !== undefined && (
                <button type="button" className="ht-button ht-button-primary"
                        onClick={() => { onClose(); onAddTask(dayKey); }}>Add a task</button>
              )}
            </>
          ) : (
            <>
              <button type="button" className="ht-button" autoFocus disabled={busy}
                      onClick={() => { setRemoving(null); setSaid(null); }}>Keep it</button>
              <button type="button" className="ht-button is-destructive" disabled={busy}
                      onClick={() => { void confirmRemove(); }}>{busy ? "Removing…" : "Remove"}</button>
            </>
          )}
        </div>
      </div>
    </dialog>
  );
}
